import { useEffect, useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { MONO_CLASS, Mono } from '../ui/Mono';
import { useWarehouseLabels } from './warehouseLabels';
import { DetectLocalNetworkBase, DiscoverNetworkPrintersViaQz } from './zplPrinting';

// Base de rede default sugerida no input (3 primeiros octetos de um /24).
const DEFAULT_BASE_IP = '192.168.0';
// Espelho leve da validação do backend (que é a autoridade): 3 octetos.
const BASE_IP_PATTERN = /^\d{1,3}\.\d{1,3}\.\d{1,3}$/;

/**
 * Descoberta de impressoras de REDE pelo QZ Tray DESTE computador: a base do
 * /24 vem do IP local (o usuário pode trocar), e cada endereço é tentado na
 * porta 9100 daqui mesmo — o servidor, na nuvem, não alcança a rede do
 * armazém. Cada host encontrado tem um "+ cadastrar" que devolve {host, port}
 * ao chamador — a página pré-preenche o formulário de criação de impressora
 * (kind NETWORK).
 */
const PrinterDiscoverModal = ({ isOpen, onClose, onRegister }) => {
    const { label } = useWarehouseLabels();
    const [baseIp, setBaseIp] = useState(DEFAULT_BASE_IP);
    const [scanning, setScanning] = useState(false);
    // null = ainda não varreu; [] = varreu e não achou nada.
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        setResults(null);
        setError(null);
        // A faixa sugerida é a da rede deste computador (pelo QZ).
        let alive = true;
        DetectLocalNetworkBase().then((base) => {
            if (alive && base) {
                setBaseIp(base);
            }
        });
        return () => {
            alive = false;
        };
    }, [isOpen]);

    const closeModal = () => {
        if (!scanning) {
            onClose();
        }
    };

    const handleScan = async (event) => {
        event.preventDefault();
        const base = baseIp.trim();
        if (!BASE_IP_PATTERN.test(base)) {
            setError({ field: 'base_ip', message: label('wms.printer.discover_invalid_base') });
            return;
        }

        setScanning(true);
        setError(null);
        setResults(null);
        try {
            setResults(await DiscoverNetworkPrintersViaQz(base, label));
        } catch (err) {
            setError({ message: err?.message || 'Erro ao varrer a rede' });
        } finally {
            setScanning(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={closeModal} title={label('wms.printer.discover')} size="sm">
            <form onSubmit={handleScan} className="space-y-4">
                <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.printer.discover_description')}</p>

                <Input
                    label={label('wms.printer.discover_base_ip')}
                    value={baseIp}
                    onChange={(event) => setBaseIp(event.target.value)}
                    placeholder={DEFAULT_BASE_IP}
                    errorMessage={error?.field === 'base_ip' ? error.message : undefined}
                    disabled={scanning}
                    autoComplete="off"
                    className={MONO_CLASS}
                />

                {error && !error.field ? (
                    <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                        {error.message}
                    </p>
                ) : null}

                {scanning ? (
                    <p className="text-sm text-mono-500 dark:text-mono-400">
                        {label('wms.printer.discover_scanning')}
                    </p>
                ) : null}

                {results !== null && results.length === 0 ? (
                    <p className="text-sm text-mono-500 dark:text-mono-400">{label('wms.printer.discover_none')}</p>
                ) : null}

                {results !== null && results.length > 0 ? (
                    <ul className="divide-y divide-mono-200 rounded-lg border border-mono-200 dark:divide-mono-800 dark:border-mono-800">
                        {results.map((found) => (
                            <li key={`${found.host}:${found.port}`} className="flex items-center justify-between gap-3 px-3 py-2">
                                <Mono className="text-sm">{`${found.host}:${found.port}`}</Mono>
                                <Button variant="ghost" size="sm" onClick={() => onRegister(found)}>
                                    {label('wms.printer.discover_register')}
                                </Button>
                            </li>
                        ))}
                    </ul>
                ) : null}

                <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                    <Button type="submit" loading={scanning} className="flex-1">
                        {label('wms.printer.discover_scan')}
                    </Button>
                    <Button type="button" onClick={closeModal} variant="secondary" className="sm:w-auto">
                        {label('wms.common.close')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default PrinterDiscoverModal;
